import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ErrorResponse } from '../../custom-fetch';
import { Route } from '../../routes';
import { QueryKey } from '../../services/query-keys';
import ErrorsAlert from '../ErrorsAlert/ErrorsAlert';
import Spinner from '../ui/Spinner/Spinner';
import { useState } from 'react';
import { useSortBy } from '../../hooks/useSortBy';
import { useReservationService } from '../../services/reservations-service';
import IReservation, { EReservationStatus } from '../../shared/IReservation';
import Input, { EInputType } from '../ui/Input/Input';
import Button, { EButtonColor, EButtonSize } from '../ui/Button/Button';
import "./ReservationsList.css";

enum EListSortBy {
  irn = "irn",
  refNo = "refNo",
  status = "status",
  dateFrom = "dateFrom",
  dateTo = "dateTo",
  timeFrom = "timeFrom",
  timeTo = "timeTo",
  departureStation = "departureStation",
  returnStation = "returnStation",
  group = "group",
  customerName = "customerName",
}

const ReservationsList: React.FC = () => {
  const { fetchReservations, exportReservations } = useReservationService();
  const [filterValue, setFilterValue] = useState<string>('');
  const [onlyActive, setOnlyActive] = useState<boolean>(true);
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const currentYear = new Date().getFullYear();
  const yearOptions = [
    { value: (currentYear + 1).toString(), label: (currentYear + 1).toString() },
    { value: currentYear.toString(), label: currentYear.toString() },
    { value: (currentYear - 1).toString(), label: (currentYear - 1).toString() },
  ];

  const monthOptions = [
    ...Array.from({ length: 12 }, (_, i) => ({
      value: (i + 1).toString(),
      label: new Date(0, i).toLocaleString('en', { month: 'long' }),
    })),
  ];

  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>('');

  const {
    data: reservations,
    isPending,
    isError,
    error,
  } = useQuery<IReservation[], ErrorResponse>({
    queryKey: [QueryKey.reservations, selectedYear, selectedMonth],
    queryFn: ({ signal }) => fetchReservations({ 
      signal, 
      year: selectedYear ? parseInt(selectedYear) : undefined,
      month: selectedMonth ? parseInt(selectedMonth) : undefined
    }),
    staleTime: 5000
  });

  const customSort = (item: IReservation, field: string) => {
    const value = item[field as keyof IReservation];
    if (field === EListSortBy.dateFrom || field === EListSortBy.dateTo) {
      if (typeof value === 'string' && value.includes('/')) {
        const parts = value.split('/');
        if (parts.length === 3) {
          return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
      }
    }
    return String(value || "");
  };

  const { sorted, sort, changeSortOrDirection, getChevron } = useSortBy(reservations, customSort);

  const searchValue = filterValue.toLowerCase().trim();

  const filteredReservations = Array.isArray(sorted) ? sorted.filter((reservation) => {
    const matchesSearch = 
      reservation.irn?.toLowerCase()?.includes(searchValue) ||
      reservation.refNo?.toLowerCase()?.includes(searchValue);
    
    const matchesActive = !onlyActive || reservation.status === EReservationStatus.RES;

    return matchesSearch && matchesActive;
  }) : [];

  if (isError) {
    return <ErrorsAlert errors={error.messages} />;
  }

  if (isPending) {
    return <Spinner />;
  }


  const openHandler = (irn: string, refNo: string) => {
    navigate(Route.reservation(refNo, irn));
  }

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const result = await exportReservations();
      if (result && result.csvString) {
        const fileName = `${new Date().toISOString().slice(0, 10)}_TCRent_bookings_${result.organizationName || 'export'}.csv`;
        const blob = new Blob(["\ufeff", result.csvString], { type: 'text/csv;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Export failed", err);
    } finally {
      setExportLoading(false);
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case EReservationStatus.RES:
      case EReservationStatus.OK:
      case EReservationStatus.SENDING:
        return <span className="px-2 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full">Confirmed</span>;
      case EReservationStatus.CNC:
        return <span className="px-2 py-1 text-xs font-semibold text-red-800 bg-red-100 rounded-full">Canceled</span>;
      case EReservationStatus.REJ:
        return <span className="px-2 py-1 text-xs font-semibold text-orange-800 bg-orange-100 rounded-full">Rejected</span>;
      case EReservationStatus.RUN:
      case EReservationStatus.RNT:
        return <span className="px-2 py-1 text-xs font-semibold text-blue-800 bg-blue-100 rounded-full">In Rental</span>;
      case EReservationStatus.IN:
        return <span className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-200 rounded-full">Returned</span>;
      case EReservationStatus.REQ:
        return <span className="px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-100 rounded-full">On request</span>;
      default:
        // Handle unknown statuses gracefully
        return <span className="px-2 py-1 text-xs font-semibold text-gray-600 bg-gray-100 rounded-full">{status}</span>;
    }
  };

  return (
    <div className="reservations-list">
      <div className="filter-section">
        <div className="search-bar">
          <div className="w-full md:w-[250px]">
            <Input
              type={EInputType.text}
              inputName="search"
              label="Search"
              value={filterValue}
              onChange={(val) => setFilterValue(val as string)}
              placeholder="Search by IRN or Reference..."
              weight={1}
              containerStyles={{ marginBottom: 0 }}
            />
          </div>
          
          <div className="only-active-wrapper">
            <Input
              type={EInputType.checkbox}
              inputName="onlyActive"
              value={onlyActive ? ['active'] : []}
              onChange={(value) => setOnlyActive((value as string[]).includes('active'))}
              options={[{ value: 'active', label: 'Only active' }]}
              containerStyles={{ marginBottom: 0 }}
            />
          </div>

          <div className="year-month-filters">
            <div className="w-full sm:w-[200px]">
              <Input
                type={EInputType.reactSelect}
                inputName="year"
                label="Year"
                placeholder="Select year"
                value={selectedYear}
                onChange={(val) => {
                  setSelectedYear(val as string);
                  if (!val) setSelectedMonth('');
                }}
                options={yearOptions}
                containerStyles={{ marginBottom: 0 }}
                menuPosition='fixed'
              />
            </div>
            <div className="w-full sm:w-[200px]">
              <Input
                type={EInputType.reactSelect}
                inputName="month"
                label="Month"
                placeholder="Select month"
                value={selectedMonth}
                onChange={(val) => setSelectedMonth(val as string)}
                options={monthOptions}
                containerStyles={{ marginBottom: 0 }}
                menuPosition='fixed'
                disabled={!selectedYear}
              />
            </div>
          </div>

          <div className="export-button-wrapper">
            <Button
              onClick={handleExport}
              loading={exportLoading}
              size={EButtonSize.SMALL}
              color={EButtonColor.SECONDARY}
              outlined
            >
              Export CSV
            </Button>
          </div>
        </div>

        <div className="table-wrapper">
          {filteredReservations && filteredReservations.length > 0 ? (
            <table className="bookings-table">
              <thead className="table-head">
                <tr>
                  {[
                    { label: 'Irn', sort: EListSortBy.irn },
                    { label: 'Reference', sort: EListSortBy.refNo },
                    { label: 'Status', sort: EListSortBy.status },
                    { label: 'Date Out', sort: EListSortBy.dateFrom },
                    { label: 'Date In', sort: EListSortBy.dateTo },
                    { label: 'Station Out', sort: EListSortBy.departureStation },
                    { label: 'Station In', sort: EListSortBy.returnStation },
                    { label: 'Driver', sort: EListSortBy.customerName },
                  ].map((col) => (
                    <th
                      key={col.label}
                      onClick={() => col.sort && changeSortOrDirection(col.sort)}
                      className="table-header-cell"
                    >
                      <div className="header-cell-content">
                        {col.label}
                        {sort === col.sort && (
                          <span className="sort-icon">
                            {getChevron()}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="table-body">
                {filteredReservations.map((booking) => (
                  <tr 
                    key={booking.irn + booking.refNo} 
                    onClick={() => openHandler(booking.irn, booking.refNo)}
                    className="table-row"
                  >
                    <td className="cell-irn">{booking.irn}</td>
                    <td className="cell-ref">{booking.refNo}</td>
                    <td className="cell-status">{getStatusBadge(booking.status)}</td>
                    <td className="cell-details">
                      <div className="date-primary">{booking.dateFrom}</div>
                      <div className="date-secondary">{booking.timeFrom}</div>
                    </td>
                    <td className="cell-details">
                      <div className="date-primary">{booking.dateTo}</div>
                      <div className="date-secondary">{booking.timeTo}</div>
                    </td>
                    <td className="cell-details">{booking.departureStation.code}</td>
                    <td className="cell-details">{booking.returnStation.code}</td>
                    <td className="cell-name">{booking.customerName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="no-bookings-message">
              No bookings found for this organization
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReservationsList;
